import React, { useState, useEffect } from "react";
import axios from "axios";
import { useCredit } from "../context/CreditContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { useToast } from "../hooks/use-toast";
import { fmtUsd, TIER_META } from "../data/casesData";
import { Truck, DollarSign, Loader2, Package, ChevronLeft, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => {
  const t = localStorage.getItem("uc_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Item-first withdraw flow with MULTI-SELECT: pick items -> Ship OR Cash out total value.
export function WithdrawPanel({ presetItems = null, presetAction = null, onDone }) {
  const { cashoutItems, requestShip } = useCredit();
  const { toast } = useToast();
  const [items, setItems] = useState(presetItems || []);
  const [loadingItems, setLoadingItems] = useState(!presetItems);
  const [selected, setSelected] = useState(presetItems || []);
  const [action, setAction] = useState(presetItems && presetAction ? presetAction : null);
  const [step, setStep] = useState(presetItems ? (presetAction ? "form" : "action") : "pick");

  useEffect(() => {
    if (presetItems) { setItems(presetItems); setSelected(presetItems); setLoadingItems(false); return; }
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/inventory`, { headers: authHeader() });
        const avail = (data.inventory || []).filter((x) => x.status === "owned" || !x.status);
        if (active) setItems(avail);
      } catch (_) {} finally { if (active) setLoadingItems(false); }
    })();
    return () => { active = false; };
  }, [presetItems]);

  const total = selected.reduce((s, i) => s + Number(i.value || 0), 0);
  const ids = selected.map((i) => i.id);
  const toggle = (it) => setSelected((s) => s.some((x) => x.id === it.id) ? s.filter((x) => x.id !== it.id) : [...s, it]);

  const BackBtn = ({ to }) => (
    <button data-testid="withdraw-back" onClick={to}
      className="flex items-center gap-1 text-[11px] font-bold uppercase text-[#8A90A0] hover:text-[#0891B2] mb-1">
      <ChevronLeft className="h-3.5 w-3.5" /> Back
    </button>
  );

  const SelectedBar = () => (
    <div className="flex items-center justify-between border border-[#2A2440] dark:border-[#2A2440] px-3 py-2 mt-1 text-sm">
      <span className="font-medium">{selected.length} item{selected.length !== 1 ? "s" : ""} selected</span>
      <span className="font-grotesk font-bold">{fmtUsd(total)}</span>
    </div>
  );

  // STEP 1 — pick items (multi-select)
  if (step === "pick") {
    return (
      <div data-testid="withdraw-panel">
        <p className="text-xs text-[#8A90A0]">Select one or more won items, then ship them or cash out their total value.</p>
        {loadingItems ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#8A90A0]" /></div>
        ) : items.length === 0 ? (
          <div data-testid="withdraw-no-items" className="mt-3 border border-dashed border-[#2A2440] dark:border-[#2A2440] p-6 text-center text-xs text-[#8A90A0] flex flex-col items-center gap-1">
            <Package className="h-6 w-6" /> No items yet. Open a case or win a battle first.
          </div>
        ) : (
          <>
            <div className="mt-2 max-h-64 overflow-y-auto grid grid-cols-1 gap-1.5" data-testid="withdraw-item-list">
              {items.map((it) => {
                const meta = TIER_META[it.tier] || TIER_META.common;
                const on = selected.some((x) => x.id === it.id);
                return (
                  <button key={it.id} data-testid="withdraw-item-option" onClick={() => toggle(it)}
                    className={`flex items-center justify-between px-3 py-2.5 border text-left ${on ? "border-[#16A34A] bg-[#16A34A]/10" : "border-[#2A2440] dark:border-[#2A2440] hover:bg-[#19142A] dark:hover:bg-[#2A2440]"}`}>
                    <span className="flex items-center gap-2 min-w-0">
                      <span className={`h-4 w-4 rounded-lg border flex items-center justify-center shrink-0 ${on ? "bg-[#16A34A] border-[#16A34A]" : "border-[#B7BECC]"}`}>
                        {on && <Check className="h-3 w-3 text-white" />}
                      </span>
                      <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: meta.color }} />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium truncate">{it.name}</span>
                        <span className="block text-[10px] uppercase text-[#8A90A0]">{meta.label}</span>
                      </span>
                    </span>
                    <span className="font-grotesk font-bold text-white dark:text-white shrink-0">{fmtUsd(it.value)}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex items-center justify-between mt-3 text-sm">
              <span className="text-[#8A90A0]">{selected.length} selected</span>
              <span className="font-grotesk font-bold">{fmtUsd(total)}</span>
            </div>
            <Button data-testid="withdraw-continue" disabled={selected.length === 0} onClick={() => setStep("action")}
              className="w-full h-11 mt-2 rounded-lg font-grotesk font-bold bg-[#7C4DFF] dark:bg-[#9945FF] hover:bg-[#6A3AE6] dark:hover:bg-[#8236e6] text-white disabled:opacity-50">
              Continue
            </Button>
          </>
        )}
      </div>
    );
  }

  // STEP 2 — choose action
  if (step === "action") {
    return (
      <div data-testid="withdraw-actions">
        {!presetItems && <BackBtn to={() => setStep("pick")} />}
        <SelectedBar />
        <p className="text-xs text-[#8A90A0] mt-3">How would you like to redeem {selected.length > 1 ? "these items" : "this item"}?</p>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <Button data-testid="withdraw-action-ship" onClick={() => { setAction("ship"); setStep("form"); }}
            className="h-auto py-3 flex-col gap-1 rounded-lg bg-[#7C4DFF] dark:bg-[#1E1832] hover:bg-[#6A3AE6] dark:hover:bg-[#2A2440] text-white">
            <Truck className="h-5 w-5" /> <span className="text-xs font-bold">Ship {selected.length > 1 ? "all" : "it"}</span>
          </Button>
          <Button data-testid="withdraw-action-cash" onClick={() => { setAction("cash"); setStep("form"); }}
            className="h-auto py-3 flex-col gap-1 rounded-lg bg-[#16A34A] hover:bg-[#15903f] text-white">
            <DollarSign className="h-5 w-5" /> <span className="text-xs font-bold">Cash out {fmtUsd(total)}</span>
          </Button>
        </div>
      </div>
    );
  }

  // STEP 3 — form
  return (
    <div>
      <BackBtn to={() => setStep(presetItems ? "pick" : "action")} />
      <SelectedBar />
      {action === "ship"
        ? <ShipForm ids={ids} label={selected.length > 1 ? `${selected.length} items` : selected[0]?.name} requestShip={requestShip} toast={toast} onDone={onDone} />
        : <CashForm ids={ids} total={total} label={selected.length > 1 ? `${selected.length} items` : selected[0]?.name} cashoutItems={cashoutItems} toast={toast} onDone={onDone} />}
    </div>
  );
}

// Standalone modal used by the inventory "Ship" button (items + action preset).
export default function WithdrawModal({ open, onOpenChange, presetItems = null, presetAction = null, onDone }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#19142A] dark:bg-[#141021] border border-[#2A2440] dark:border-[#2A2440] rounded-lg max-w-md text-white dark:text-white">
        <DialogHeader><DialogTitle className="font-cases uppercase">Withdraw</DialogTitle></DialogHeader>
        <WithdrawPanel presetItems={presetItems} presetAction={presetAction}
          onDone={() => { onDone && onDone(); onOpenChange(false); }} />
      </DialogContent>
    </Dialog>
  );
}

function ShipForm({ ids, label, requestShip, toast, onDone }) {
  const [form, setForm] = useState({ full_name: "", address1: "", city: "", state: "", zip: "", country: "", note: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setErr("");
    for (const k of ["full_name", "address1", "city", "state", "zip", "country"]) {
      if (!form[k].trim()) { setErr("Please complete all required fields"); return; }
    }
    setLoading(true);
    try {
      await requestShip(ids, form);
      toast({ title: "Shipping requested", description: `${label} — pending review.` });
      onDone && onDone();
    } catch (e) { setErr(e?.response?.data?.detail || "Could not submit shipping request"); setLoading(false); }
  };

  const field = (k, l, ph) => (
    <div>
      <label className="text-[10px] font-semibold uppercase text-[#8A90A0]">{l}</label>
      <input data-testid={`ship-${k}`} value={form[k]} onChange={(e) => { set(k, e.target.value); setErr(""); }} placeholder={ph}
        className="w-full mt-0.5 border border-[#2A2440] dark:border-[#2A2440] bg-[#19142A] dark:bg-[#1E1832] py-2 px-2.5 text-sm outline-none text-white dark:text-white placeholder:text-[#B7BECC]" />
    </div>
  );

  return (
    <div data-testid="withdraw-ship-panel" className="mt-2">
      <div className="grid grid-cols-2 gap-2">
        <div className="col-span-2">{field("full_name", "Full name", "Jane Doe")}</div>
        <div className="col-span-2">{field("address1", "Street address", "123 Main St, Apt 4")}</div>
        {field("city", "City", "Miami")}
        {field("state", "State / Region", "FL")}
        {field("zip", "ZIP / Postal", "33101")}
        {field("country", "Country", "USA")}
        <div className="col-span-2">{field("note", "Note (optional)", "Delivery instructions")}</div>
      </div>
      {err && <p data-testid="ship-error" className="text-xs text-[#DB2777] mt-2">{err}</p>}
      <Button data-testid="ship-submit" disabled={loading} onClick={submit}
        className="w-full h-11 mt-3 rounded-lg font-grotesk font-bold bg-[#7C4DFF] dark:bg-[#9945FF] hover:bg-[#6A3AE6] dark:hover:bg-[#8236e6] text-white disabled:opacity-50">
        {loading ? "Submitting…" : "Request shipping"}
      </Button>
    </div>
  );
}

function CashForm({ ids, total, label, cashoutItems, toast, onDone }) {
  const [method, setMethod] = useState("paypal");
  const [destination, setDestination] = useState("");
  const [bank, setBank] = useState({ account_holder: "", account_number: "", routing_number: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const setB = (k, v) => setBank((b) => ({ ...b, [k]: v }));

  const submit = async () => {
    setErr("");
    const payload = { method };
    if (method === "bank") {
      if (!bank.account_holder.trim() || !bank.account_number.trim() || !bank.routing_number.trim()) {
        setErr("Enter account holder, account number and routing number"); return;
      }
      if (!/^\d{6,17}$/.test(bank.account_number.trim())) { setErr("Enter a valid account number"); return; }
      if (!/^\d{9}$/.test(bank.routing_number.trim())) { setErr("Routing number must be 9 digits"); return; }
      Object.assign(payload, {
        account_holder: bank.account_holder.trim(),
        account_number: bank.account_number.trim(),
        routing_number: bank.routing_number.trim(),
      });
    } else {
      if (!destination.trim()) { setErr("Enter your payout details"); return; }
      if (method === "paypal" && !destination.includes("@")) { setErr("Enter a valid PayPal email"); return; }
      payload.destination = destination.trim();
    }
    setLoading(true);
    try {
      await cashoutItems(ids, payload);
      toast({ title: "Cash-out requested", description: `${fmtUsd(total)} for ${label} — pending review.` });
      onDone && onDone();
    } catch (e) { setErr(e?.response?.data?.detail || "Could not submit cash-out"); setLoading(false); }
  };

  const bankField = (k, l, ph, extra) => (
    <div>
      <label className="text-[10px] font-semibold uppercase text-[#8A90A0]">{l}</label>
      <input data-testid={`withdraw-bank-${k}`} value={bank[k]} onChange={(e) => { setB(k, e.target.value); setErr(""); }} placeholder={ph} {...(extra || {})}
        className="w-full mt-0.5 border border-[#2A2440] dark:border-[#2A2440] bg-[#19142A] dark:bg-[#1E1832] py-2 px-2.5 text-sm outline-none text-white dark:text-white placeholder:text-[#B7BECC]" />
    </div>
  );

  return (
    <div data-testid="withdraw-cash-panel" className="mt-2">
      <p className="text-xs text-[#8A90A0]">You'll receive <span className="font-bold text-[#16A34A]">{fmtUsd(total)}</span> once approved. Paid out manually after review.</p>
      <label className="text-xs font-semibold uppercase text-[#8A90A0] block mt-3">Payout method</label>
      <div className="mt-1 grid grid-cols-3 gap-2">
        {[["paypal", "PayPal"], ["bank", "Bank"], ["other", "Other"]].map(([v, l]) => (
          <button key={v} data-testid={`withdraw-method-${v}`} onClick={() => { setMethod(v); setErr(""); }}
            className={`py-2 text-xs font-bold border ${method === v ? "bg-[#7C4DFF] dark:bg-[#9945FF] text-white border-transparent" : "border-[#2A2440] dark:border-[#2A2440] text-[#8A90A0]"}`}>
            {l}
          </button>
        ))}
      </div>

      {method === "bank" ? (
        <div className="grid grid-cols-2 gap-2 mt-2">
          <div className="col-span-2">{bankField("account_holder", "Account holder name", "Jane Doe")}</div>
          <div className="col-span-2">{bankField("account_number", "Account number", "000123456789", { inputMode: "numeric" })}</div>
          <div className="col-span-2">{bankField("routing_number", "Routing number (9 digits)", "021000021", { inputMode: "numeric", maxLength: 9 })}</div>
        </div>
      ) : (
        <input data-testid="withdraw-destination-input" value={destination} onChange={(e) => { setDestination(e.target.value); setErr(""); }}
          placeholder={method === "paypal" ? "PayPal email" : "Cash App tag, crypto address, etc."}
          className="w-full mt-2 border border-[#2A2440] dark:border-[#2A2440] bg-[#19142A] dark:bg-[#1E1832] py-2.5 px-3 text-sm font-grotesk outline-none text-white dark:text-white placeholder:text-[#B7BECC]" />
      )}

      {err && <p data-testid="withdraw-error" className="text-xs text-[#DB2777] mt-2">{err}</p>}
      <Button data-testid="withdraw-cash-submit" disabled={loading} onClick={submit}
        className="w-full h-11 mt-3 rounded-lg font-grotesk font-bold bg-[#16A34A] hover:bg-[#15903f] text-white disabled:opacity-50">
        {loading ? "Submitting…" : `Receive ${fmtUsd(total)} in cash`}
      </Button>
      <p className="text-[10px] text-[#8A90A0] mt-2 text-center">We never store card numbers. Payouts are sent manually after review.</p>
    </div>
  );
}
